/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { LineAreaChartProps, LineAreaItem } from './lineAreaChart.types';
import {
  findNearestDataPoint,
  getStartAndEndAxesOffset
} from '#utils/PRIVATE_chartUtils/layoutUtils';
import { AreaSeries } from '#PRIVATE_LineAreaComponents/AreaSeries';
import { getCumulativeSeries, getMarkers, getSeriesData } from '#utils/PRIVATE_chartUtils/utils';
import { MarkersWithLabel } from '#PRIVATE_Chart/MarkersWithLabel';
import { getLineAreaChartNavUtil } from '#utils/PRIVATE_chartUtils/navUtils';
import { Chart } from '#PRIVATE_Chart';
import { Scale, TimeAxisProps, SizeHandle } from '#utils/UNSAFE_visTypes/chart';
import { useUser } from '#hooks/UNSAFE_useUser';
import { getColorRamp } from '#utils/UNSAFE_visUtils';
import { LineSeries } from '#PRIVATE_LineAreaComponents/LineSeries';
import { Point } from '#utils/PRIVATE_visSVGUtils';
import { getLineAreaSegments } from '#utils/PRIVATE_chartUtils/utils';
import { colorSchemeVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { getItemColor, getMarkerType } from '#utils/PRIVATE_chartUtils/dataStyleUtils';
import { getLineAreaChartItemPosition } from '#PRIVATE_LineAreaComponents/utils/chartUtils';
import { forwardRef, Ref } from 'preact/compat';
import { VNode } from 'preact';

const LINE_WITH_AREA_OPACITY = 0.2;
export type ForwardRefLineAreaChart = <K extends string | number, D extends LineAreaItem<K>>(
  props: LineAreaChartProps<K, D> & {
    ref?: Ref<SizeHandle>;
  }
) => VNode | null;

/**
 * The LineAreaChart displays information graphically using lines and filled areas, making relationships among the data easier to understand.
 */
export const LineAreaChart = forwardRef(
  <K extends string | number, D extends LineAreaItem<K>>(
    {
      width,
      height,
      selectedIds = [],
      hiddenIds = [],
      highlightedIds,
      hideAndShowBehavior = 'none',
      orientation = 'vertical',
      yAxis,
      y2Axis,
      series,
      groups,
      isStacked = false,
      getDataItem,
      selectionMode = 'none',
      drilling = 'off',
      type = 'line',
      overview = 'off',
      dataLabelEffect = 'outline',
      ...props
    }: LineAreaChartProps<K, D>,
    ref: Ref<SizeHandle>
  ) => {
    const { direction, forcedColors } = useUser();
    const isHighContrast = forcedColors === 'active';
    const isTestEnv = props.testId !== undefined;
    const isRtl = direction === 'rtl';
    const isLog = yAxis?.scale === 'log';
    const timeAxisType = (props.xAxis as TimeAxisProps)?.timeAxisType;
    const hiddenSet = new Set(hiddenIds);
    const selectedSet = new Set(selectedIds);
    const highlightedSet = new Set(highlightedIds);
    const isHoriz = orientation === 'horizontal';
    const colors = getColorRamp();
    let renderedSeries = isStacked
      ? getCumulativeSeries(series.length, groups.length, getDataItem, hiddenSet, isLog)
      : getSeriesData(series?.length, groups?.length, getDataItem, hiddenSet, isLog);

    const renderedGetDataItem = (seriesIndex: number, groupIndex: number): D | undefined => {
      if (!isStacked) return getDataItem(seriesIndex, groupIndex);
      return {
        ...getDataItem(seriesIndex, groupIndex),
        value: renderedSeries[seriesIndex][groupIndex]
      } as D;
    };

    const getGapRatio = () => {
      return 1;
    };

    const getNextChartItemFunc = (startIndex: number, endIndex: number) => {
      // TODO: JET-71332
      //@ts-ignore
      return getLineAreaChartNavUtil<D>(
        getDataItem,
        series.length,
        startIndex,
        endIndex,
        isStacked,
        isRtl,
        isHoriz
      );
    };

    const getDataItemPos = (xScale: Scale, yScale: Scale, y2Scale: Scale) => {
      return (seriesIndex: number, groupIndex: number) => {
        const isAssociatedToY2 = series[seriesIndex].associatedYAxis === 'y2';
        return getLineAreaChartItemPosition(
          seriesIndex,
          groupIndex,
          renderedSeries,
          getDataItem,
          groups,
          isHoriz,
          xScale,
          isAssociatedToY2 ? y2Scale : yScale,
          timeAxisType
        );
      };
    };

    const getMarkerColorAndType = (seriesIndex: number, groupIndex: number) => {
      return {
        color: getItemColor(seriesIndex, groupIndex, series, getDataItem),
        type: getMarkerType(seriesIndex, groupIndex, getDataItem)
      };
    };

    const findNearest = (xScale: Scale, yScale: Scale, y2Scale: Scale) => {
      const getSeriesDataInfo = () => {
        return {
          offset: 0,
          dataWidth: 0
        };
      };

      const getYCoord = (value: number, yScale: Scale) => {
        return yScale.transform(value);
      };

      return findNearestDataPoint<K, D>(
        series,
        groups,
        xScale,
        yScale,
        renderedGetDataItem,
        isStacked,
        getSeriesDataInfo,
        getYCoord,
        isLog,
        0,
        hiddenSet,
        timeAxisType,
        y2Scale
      );
    };
    const { hasCenteredSeries, hasUncenteredSeries } = series.reduce(
      (acc, s) => {
        return {
          hasCenteredSeries:
            acc.hasCenteredSeries ||
            s.lineType === 'centeredSegmented' ||
            s.lineType === 'centeredStepped',
          hasUncenteredSeries:
            acc.hasUncenteredSeries || s.lineType === 'segmented' || s.lineType === 'stepped'
        };
      },
      { hasCenteredSeries: false, hasUncenteredSeries: false }
    );

    const getOffset = (groupCount: number) => {
      return getStartAndEndAxesOffset(hasCenteredSeries, hasUncenteredSeries, isHoriz, groupCount);
    };

    const markers = getMarkers();
    const areaOpacity = type === 'lineWithArea' ? LINE_WITH_AREA_OPACITY : 1;
    const isAreaRendered = type === 'area' || type === 'lineWithArea';
    const isContrastLineNeeded = type === 'area';
    const allLineSegmentCoords: Point[][] = [];

    return (
      <Chart
        ref={ref}
        width={width}
        height={height}
        selectedIds={selectedSet}
        hiddenIds={hiddenIds}
        hideAndShowBehavior={hideAndShowBehavior}
        orientation={orientation}
        yAxis={yAxis}
        y2Axis={y2Axis}
        series={series}
        groups={groups}
        selectionMode={selectionMode}
        drilling={drilling}
        isStacked={isStacked}
        getDataItem={getDataItem}
        findNearest={findNearest}
        getDataItemPos={getDataItemPos}
        getMarkerInfo={getMarkerColorAndType}
        renderGridLinesInFront={isAreaRendered}
        getNavUtil={getNextChartItemFunc}
        getGapRatio={getGapRatio}
        getStartAndEndAxesOffset={getOffset}
        isRtl={isRtl}
        overview={overview}
        defaultOverviewContentRenderer={() => {
          return (
            <OverviewLineAreaChart
              series={series}
              groups={groups}
              getDataItem={getDataItem}
              type={type}
            />
          );
        }}
        {...props}>
        {({
          getItemAriaLabel,
          groupsInfo,
          xStartIndex,
          xEndIndex,
          xScale,
          yScale,
          y2Scale,
          activeId,
          focusedItemInfo,
          hoveredItemInfo,
          isPointInsideMarquee,
          defaultFontSize,
          getTextDimensions,
          plotAreaClipPathId,
          axisStepWidth
        }) => {
          const leafGroups = groupsInfo.map(({ item }) => item);
          renderedSeries = isStacked
            ? getCumulativeSeries(series.length, leafGroups.length, getDataItem, hiddenSet, isLog)
            : getSeriesData(series?.length, leafGroups?.length, getDataItem, hiddenSet, isLog);
          return (
            <>
              {renderedSeries.map((lineAreaSeries, index) => {
                if (lineAreaSeries.length === 0) {
                  /* TODO: need a better way to handle when a series is hidden. */
                  return;
                }
                const seriesItem = series[index];
                const isAssociatedToY2 = seriesItem.associatedYAxis === 'y2';
                const lineSegmentCoords: Point[][] = getLineAreaSegments<K, D>(
                  isAssociatedToY2 ? y2Scale : yScale,
                  xScale,
                  lineAreaSeries,
                  xStartIndex,
                  xEndIndex,
                  isLog,
                  leafGroups,
                  index,
                  renderedGetDataItem,
                  timeAxisType
                );
                allLineSegmentCoords[index] = lineSegmentCoords[0];
                const isBottomSegmentCurved =
                  seriesItem.lineType === 'curved' &&
                  isStacked &&
                  renderedSeries.length > 1 &&
                  index > 0;

                const isHighlighted =
                  highlightedIds === undefined ||
                  highlightedIds.length === 0 ||
                  highlightedSet.has(seriesItem.id);

                const prevCoords =
                  isStacked && index != 0
                    ? allLineSegmentCoords.reduce((prevValue, newValue, j) => {
                      // find the index i such that i < index, and series i  has at least
                      // one or more data item unhidden. Only such series will can be
                      // used as bottomCoord for current series. Otherwise we have to use
                      // baseline coord
                      if (j >= index) {
                        return prevValue;
                      }
                      if (newValue.length > 0) {
                        return j;
                      }
                      return prevValue;
                    }, 0)
                    : undefined;

                return (
                  <>
                    {isAreaRendered && (
                      <AreaSeries
                        yScale={isAssociatedToY2 ? y2Scale : yScale}
                        lineType={seriesItem.lineType}
                        color={seriesItem.areaColor || colors[index % colors.length]}
                        isHorizontal={isHoriz}
                        areaColorOpacity={areaOpacity}
                        isBottomSegmentCurved={isBottomSegmentCurved}
                        bottomCoords={
                          prevCoords !== undefined &&
                            allLineSegmentCoords[prevCoords].some((value) => value != null)
                            ? allLineSegmentCoords[prevCoords]
                            : undefined
                        }
                        lineSegments={lineSegmentCoords}
                        isLog={isLog}
                        isHighlighted={isHighlighted}
                        seriesIndex={index}
                        groupWidth={axisStepWidth}
                        isRtl={isRtl}
                        timeAxisType={timeAxisType}
                        plotAreaClipPathId={plotAreaClipPathId}
                      />
                    )}
                    <LineSeries
                      lineType={seriesItem.lineType}
                      /* TODO: confirm with UX if this is contrastLine (changes depending on dark/light mode) or always white. 
                        It's always white in legacy.*/
                      lineColor={
                        isContrastLineNeeded
                          ? colorSchemeVars.dvt.contrastLine
                          : seriesItem.lineColor
                      }
                      /* The area generated in legacy are bit apart and not touching when stacked. Preact areas are a bit closer than legacy when curved. 
                      Increased the contrast linewidth (to 2.5 from legacy 1.25) to give legacy visual treatment.
                      TODO: investivage why legacy areas are not touching. maybe we want to do the same in preact svg utils that
                      generates this curve.
                     */
                      lineWidth={
                        isContrastLineNeeded ? (isStacked ? 2.5 : 1.25) : seriesItem.lineWidth
                      }
                      lineStyle={seriesItem.lineStyle}
                      color={seriesItem.lineColor || colors[index % colors.length]}
                      lineSegmentCoords={lineSegmentCoords}
                      isHorizontal={isHoriz}
                      isHighlighted={isHighlighted}
                      seriesIndex={index}
                      groupWidth={axisStepWidth}
                      isRtl={isRtl}
                      timeAxisType={timeAxisType}
                      plotAreaClipPathId={plotAreaClipPathId}
                    />
                    {
                      /* For non stacked charts, markers should render in the order of series. Some markers might get hidden behind area but this preserves the legacy behavior.
                      JET-75357: Segmented and stepped lines may stack markers/lines incorrectly by series order, so excluded from non-stacked charts.
                     */
                      !isStacked &&
                      seriesItem.lineType !== 'stepped' &&
                      seriesItem.lineType !== 'segmented' && (
                        <MarkersWithLabel
                          yScale={isAssociatedToY2 ? y2Scale : yScale}
                          xScale={xScale}
                          groups={leafGroups}
                          series={seriesItem}
                          timeAxisType={timeAxisType}
                          color={
                            seriesItem.lineColor ||
                            seriesItem.areaColor ||
                            colors[
                            index % colors.length
                            ] /* TODO : make this use getItemColor function from dataStyleUtils. */
                          }
                          seriesIndex={index}
                          startIndex={xStartIndex}
                          endIndex={xEndIndex}
                          markerType={markers[index % markers.length]}
                          orientation={orientation}
                          isLog={isLog}
                          activeId={activeId}
                          getDataItem={getDataItem}
                          isPointInsideMarquee={isPointInsideMarquee}
                          selectedIds={selectedSet}
                          highlightedIds={
                            highlightedIds !== undefined ? highlightedSet : undefined
                          }
                          focusedItemIndex={
                            focusedItemInfo.seriesIndex === index &&
                              focusedItemInfo.isFocusVisible &&
                              !(
                                props.isDataCursorEnabled &&
                                props.dataCursorStyle?.isMarkerDisplayed
                              )
                              ? focusedItemInfo.groupIndex
                              : undefined
                          }
                          hoveredItemIndex={
                            hoveredItemInfo?.isCurrent && hoveredItemInfo.seriesIndex === index
                              ? hoveredItemInfo.groupIndex
                              : undefined
                          }
                          isSelectionEnabled={
                            selectionMode === 'single' || selectionMode === 'multiple'
                          }
                          isDrillEnabled={drilling === 'on'}
                          defaultFontSize={defaultFontSize}
                          dataLabelEffect={dataLabelEffect}
                          isHighContrast={isHighContrast}
                          isTestEnv={isTestEnv}
                          getTextDimensions={getTextDimensions}
                          getItemAriaLabel={getItemAriaLabel}
                          isRtl={isRtl}
                        />
                      )
                    }
                  </>
                );
              })}
              {renderedSeries.map((lineAreaSeries, index) => {
                if (lineAreaSeries.length === 0) {
                  /* TODO: need a better way to handle when a series is hidden.*/
                  return;
                }
                const seriesItem = series[index];
                const isAssociatedToY2 = seriesItem.associatedYAxis === 'y2';
                return isStacked ||
                  seriesItem.lineType === 'segmented' ||
                  seriesItem.lineType === 'stepped' ? (
                  <MarkersWithLabel
                    yScale={isAssociatedToY2 ? y2Scale : yScale}
                    series={series[index]}
                    xScale={xScale}
                    groups={leafGroups}
                    timeAxisType={timeAxisType}
                    color={
                      seriesItem.lineColor || seriesItem.areaColor || colors[index % colors.length]
                    }
                    seriesIndex={index}
                    isPointInsideMarquee={isPointInsideMarquee}
                    startIndex={xStartIndex}
                    endIndex={xEndIndex}
                    markerType={markers[index % markers.length]}
                    orientation={orientation}
                    isLog={isLog}
                    activeId={activeId}
                    getDataItem={renderedGetDataItem}
                    selectedIds={selectedSet}
                    focusedItemIndex={
                      focusedItemInfo.seriesIndex === index && focusedItemInfo.isFocusVisible
                        ? focusedItemInfo.groupIndex
                        : undefined
                    }
                    hoveredItemIndex={
                      hoveredItemInfo?.isCurrent && hoveredItemInfo.seriesIndex === index
                        ? hoveredItemInfo.groupIndex
                        : undefined
                    }
                    isSelectionEnabled={selectionMode === 'single' || selectionMode === 'multiple'}
                    isDrillEnabled={drilling === 'on'}
                    defaultFontSize={defaultFontSize}
                    dataLabelEffect={dataLabelEffect}
                    isHighContrast={isHighContrast}
                    isTestEnv={isTestEnv}
                    getTextDimensions={getTextDimensions}
                    getItemAriaLabel={getItemAriaLabel}
                    isRtl={isRtl}
                  />
                ) : undefined;
              })}
            </>
          );
        }}
      </Chart>
    );
  }
) as ForwardRefLineAreaChart;

function OverviewLineAreaChart<K extends string | number, D extends LineAreaItem<K>>(
  props: LineAreaChartProps<K, D>
) {
  return (
    <LineAreaChart
      datatip={() => ({ content: '' })}
      zoomAndScroll="off"
      overview="off"
      plotArea={{
        yMajorTick: {
          isRendered: false
        }
      }}
      xAxis={{
        tickLabel: {
          autoRotate: false,
          // @ts-ignore
          isRendered: props.xAxis?.timeAxisType ? true : false
        }
      }}
      series={props.series}
      groups={props.groups}
      getDataItem={props.getDataItem}
      yAxis={{
        tickLabel: {
          isRendered: false
        }
      }}
      type={props.type}
    />
  );
}
